import React from 'react';
import { ReviewFilterType } from '../types';
import { Search, X } from 'lucide-react';

interface AdminReviewFiltersProps {
  activeFilter: ReviewFilterType;
  onFilterChange: (filter: ReviewFilterType) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  providerIdFilter: string;
  onProviderIdChange: (id: string) => void;
  userIdFilter: string;
  onUserIdChange: (id: string) => void;
}

export const AdminReviewFilters: React.FC<AdminReviewFiltersProps> = ({
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  providerIdFilter,
  onProviderIdChange,
  userIdFilter,
  onUserIdChange,
}) => {
  const filters: { id: ReviewFilterType; label: string }[] = [
    { id: 'all', label: 'All Reviews' },
    { id: 'flagged', label: 'Flagged' },
    { id: 'low-rating', label: 'Low Rating' },
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
  ];

  const clearFilters = () => {
    onSearchChange('');
    onProviderIdChange('');
    onUserIdChange('');
    onFilterChange('all');
  };

  const hasActiveFilters = searchQuery || providerIdFilter || userIdFilter || activeFilter !== 'all';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <X className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => (
          <button
            key={filter.id}
            onClick={() => onFilterChange(filter.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === filter.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search by review content..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="providerId" className="block text-sm font-medium text-gray-700 mb-1">
            Provider ID
          </label>
          <input
            id="providerId"
            type="text"
            placeholder="Filter by provider ID"
            value={providerIdFilter}
            onChange={(e) => onProviderIdChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-1">
            User ID
          </label>
          <input
            id="userId"
            type="text"
            placeholder="Filter by user ID"
            value={userIdFilter}
            onChange={(e) => onUserIdChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
    </div>
  );
};
